import {
  SERVICE_TIER_PUBLIC,
  SERVICE_TIER_PRO,
  SERVICE_TIER_FREEMIUM,
  ServiceTier,
  SERVICE_TIER_UNKNOWN,
  PRO_USER_CORE_ROLE,
} from '../constants/Constants';
import { isInRole } from '../jwt/JwtReader';
import { IEmServiceDetails, ILoggerDetails } from '../types/types';
import { LogMsg } from './Logging';

/**
 * Retrieves the current active service tiers from the service details.
 * @param {IEmServiceDetails} emServiceDetails - The service details containing tier-to-route mappings
 * @returns {ServiceTier[]} - Array of service tiers that are currently active
 * @throws {Error} - Throws an error if there are 0 or 2+ mappings with isOnNow=true
 */
export function getCurrentTiers(emServiceDetails: IEmServiceDetails, logger?: ILoggerDetails): ServiceTier[] {
  LogMsg(logger, `getCurrentTiers() :: emServiceDetails: ${JSON.stringify(emServiceDetails)}`);

  // If this service doesn't have proper tier mappings - it has to be fixed

  const activeMappings = emServiceDetails.tierToRouteMapping.filter((_mapping) => _mapping.isOnNow);

  if (activeMappings.length === 0) {
    throw new Error('getCurrentTiers() :: No active tier-to-route mappings found');
  }
  if (activeMappings.length > 1) {
    throw new Error(`getCurrentTiers() :: Multiple active tier-to-route mappings found (${activeMappings.length})`);
  }

  return activeMappings[0].tiers;
}

/**
 * Retrieves the non-active (alternative, aka. isOnNow==false) service tiers from the service details.
 * @param {IEmServiceDetails} emServiceDetails - The service details containing tier-to-route mappings
 * @returns {ServiceTier[]} - Array of service tiers that are currently NOT-active
 */
export function getAlternativeTiers(emServiceDetails: IEmServiceDetails, logger?: ILoggerDetails): ServiceTier[] {
  LogMsg(logger, `getAlternativeTiers() :: emServiceDetails: ${JSON.stringify(emServiceDetails)}`);

  if (!emServiceDetails?.tierToRouteMapping || emServiceDetails.tierToRouteMapping.length === 0) {
    throw new Error('getAlternativeTiers() :: No tier-to-route mappings found');
  }

  const alternativeMappings = emServiceDetails.tierToRouteMapping.filter((mapping) => !mapping.isOnNow);

  if (alternativeMappings.length === 0) {
    return [];
  }

  // Combine all tiers from alternative mappings into a single array
  const combinedTiers = alternativeMappings.flatMap((mapping) => mapping.tiers);
  return combinedTiers;
}

/**
 * Validates if the service details have exactly one active tier mapping
 * @param {IEmServiceDetails} emServiceDetails - The service details containing tier-to-route mappings
 * @returns {string|null} null if object is valid, else error message with desc
 */
export function getEmServiceErrors(emServiceDetails: IEmServiceDetails, logger?: ILoggerDetails): string | null {
  LogMsg(logger, `getEmServiceErrors() :: for emServiceDetails: ${JSON.stringify(emServiceDetails)}`);

  if (!emServiceDetails) {
    return `emServiceDetails object not defined`;
  }

  if (!emServiceDetails?.tierToRouteMapping || emServiceDetails.tierToRouteMapping.length === 0) {
    //
    return `tierToRouteMapping are empty`;
  }

  // Find duplicate tiers across mappings
  const tierSet = new Set<ServiceTier>();
  for (const mapping of emServiceDetails.tierToRouteMapping) {
    for (const tier of mapping.tiers) {
      if (tierSet.has(tier)) {
        return `found same tier(s) used in multiple mappings for tier: ${tier}`; // Duplicate found
      }
      tierSet.add(tier);
    }
  }

  const activeMappings = emServiceDetails.tierToRouteMapping.filter((mapping) => mapping.isOnNow);

  if (activeMappings.length !== 1) {
    return `multiple tierToRouteMapping ${activeMappings.length} entries with isOnNow:true`;
  }

  return null;
}

/**
 * Checks if the service is a public-only service
 * @param {IEmServiceDetails} emService - The service details
 * @returns {boolean} True if service is public-only
 */
export function isPublicService(emService: IEmServiceDetails, logger?: ILoggerDetails): boolean {
  LogMsg(logger, `isPublicService() :: checking for ${JSON.stringify(emService)}`);

  const currTiers = getCurrentTiers(emService, logger);
  return currTiers.length === 1 && currTiers[0] === SERVICE_TIER_PUBLIC;
}

/**
 * Checks if the service is a PRO-only service
 * @param {IEmServiceDetails} emService - The service details
 * @returns {boolean} True if service is PRO-only
 */
export function isProService(emService: IEmServiceDetails, logger?: ILoggerDetails): boolean {
  LogMsg(logger, `isProService() :: checking for ${JSON.stringify(emService)}`);

  const currTiers = getCurrentTiers(emService, logger);
  return currTiers.length === 1 && currTiers[0] === SERVICE_TIER_PRO;
}

/**
 * Checks if the service is a FREEMIUM-only service
 * @param {IEmServiceDetails} emService - The service details
 * @returns {boolean} True if service is FREEMIUM-only
 */
export function isFreemiumService(emService: IEmServiceDetails, logger?: ILoggerDetails): boolean {
  LogMsg(logger, `isFreemiumService() :: checking for ${JSON.stringify(emService)}`);

  const currTiers = getCurrentTiers(emService, logger);
  return currTiers.length === 1 && currTiers[0] === SERVICE_TIER_FREEMIUM;
}

/**
 * Checks if the service includes support (allows access to) ANONYMOUS (aka. "PUBLIC") users
 * @param {IEmServiceDetails} emService - The service details
 * @returns {boolean} True if service supports PRO users
 */
export function supportsPublicUsers(emService: IEmServiceDetails, logger?: ILoggerDetails): boolean {
  LogMsg(logger, `supportsPublicUsers() :: checking for ${JSON.stringify(emService)}`);
  return getCurrentTiers(emService, logger).includes(SERVICE_TIER_PUBLIC);
}

/**
 * Checks if the service supports PRO users (either PRO-only or multi-tier)
 * @param {IEmServiceDetails} emService - The service details
 * @returns {boolean} True if service supports PRO users
 */
export function supportsProUsers(emService: IEmServiceDetails, logger?: ILoggerDetails): boolean {
  LogMsg(logger, `supportsProUsers() :: checking for ${JSON.stringify(emService)}`);

  return getCurrentTiers(emService, logger).includes(SERVICE_TIER_PRO);
}

/**
 * Checks if the service supports FREEMIUM users (either FREEMIUM-only or multi-tier)
 * @param {IEmServiceDetails} emService - The service details
 * @returns {boolean} True if service supports FREEMIUM users
 */
export function supportsFreemiumUsers(emService: IEmServiceDetails, logger?: ILoggerDetails): boolean {
  LogMsg(logger, `supportsFreemiumUsers() :: checking for ${JSON.stringify(emService)}`);

  return getCurrentTiers(emService, logger).includes(SERVICE_TIER_FREEMIUM);
}

/**
 * Checks if the current service supports authenticated users (PRO, FREEMIUM, or multi-tier)
 * @param {IEmServiceDetails} emService - The service details
 * @returns {boolean} True if service supports authenticated users
 */
export function isLoggedInOnlyService(emService: IEmServiceDetails, logger?: ILoggerDetails): boolean {
  return (
    (supportsProUsers(emService, logger) || supportsFreemiumUsers(emService, logger)) &&
    !supportsPublicUsers(emService, logger)
  );
}

export function hasProAlternativeService(emService: IEmServiceDetails, logger?: ILoggerDetails): boolean {
  return getAlternativeTiers(emService, logger).includes(SERVICE_TIER_PRO);
}

export function hasFreemiumAlternativeService(emService: IEmServiceDetails, logger?: ILoggerDetails): boolean {
  return getAlternativeTiers(emService, logger).includes(SERVICE_TIER_FREEMIUM);
}

export function hasPublicAlternativeService(emService: IEmServiceDetails, logger?: ILoggerDetails): boolean {
  return getAlternativeTiers(emService, logger).includes(SERVICE_TIER_PUBLIC);
}

/**
 * Checks if the service supports authenticated users (PRO, FREEMIUM, or multi-tier)
 * @param {IEmServiceDetails} emService - The service details
 * @returns {boolean} True if service supports authenticated users
 */
export function hasLoggedInAlternativeService(emService: IEmServiceDetails, logger?: ILoggerDetails): boolean {
  return hasProAlternativeService(emService, logger) || hasFreemiumAlternativeService(emService, logger);
}

export function getServiceTierByUser(em_at?: string): ServiceTier {
  if (em_at) {
    const isProUser = isInRole(em_at, PRO_USER_CORE_ROLE);
    if (isProUser) {
      return SERVICE_TIER_PRO;
    } else {
      return SERVICE_TIER_FREEMIUM;
    }
  }

  return SERVICE_TIER_UNKNOWN;
}
